import { useEffect, useState, useRef, useMemo } from "react";

import { Box } from "@mui/material";
import { withTranslation } from "react-i18next";
import reduxUtil from "@plannel/utils/redux-util";
import {difference, groupBy, keys, size, isEmpty, divide, sum, isNumber, mean } from "lodash";
import CodeUtil from "@plannel/pages/utils/code-util";
import dateUtils from "@plannel/utils/date-util";
import numberUtil from "@plannel/utils/number-util";
import TypeToggleButton from "@plannel/components/ToggleButton";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import { DashboardPeriodCaption, baseDatesToPeriod } from "@plannel/components/dashboard/DashboardLayout";
import DashboardComponent from "@plannel/components/layout/DashboardComponent";
import LocationFilter from "@plannel/components/filter/LocationFilter";
import ItemHrchyTreeFilter from "@plannel/components/filter/ItemHrchyTreeFilter";
import dashboardService from "@plannel/services/dashboard/dashboard-service";
import { useDispatch } from "react-redux";

const { toggleType: { QTY, AMT } } = CodeUtil;

/* RP Tab Name */
const RP_ARRIVAL = "ARRIVAL";
const RP_PURCHASE = "PURCHASE";
const RP_SUPPLY = "SUPPLY";

const ARRIVAL_PLAN = "arrivalPlan";
const ARRIVAL = "arrival";
const ACT_ARRIVAL = "actArrival";
const PURCHASE = "purchaseBudget";
const SUPPLY = "shipmentTab";
const DEMAND_PLAN = "demandPlan";
const SALES = "sales";
const INVENTORY = "inventory";
const GROSS_PROFIT = "grossProfit";
const LOSS_OF_OPPORTUNITY = "lossOfOpportunity";
const INV_TURNOVER = "invTurnover";
const INV_TURNOVER_COGS = "invTurnoverCogs";
const GROSS_MARGIN = "grossMarginPer";
const CROSSOVER_RATIO = "crossoverRatioPer";
const VS_ARRIVAL_PLAN = "planVsArrivalPer";
const FILLRATE = "fillRatePer";
const SHPT = "shipment";

const RP_DASHBOARD_AMT_MEASURES = [ARRIVAL_PLAN, DEMAND_PLAN, ACT_ARRIVAL, SALES, SHPT, INVENTORY, PURCHASE, GROSS_PROFIT, LOSS_OF_OPPORTUNITY, INV_TURNOVER, INV_TURNOVER_COGS, GROSS_MARGIN, CROSSOVER_RATIO, VS_ARRIVAL_PLAN, FILLRATE];
const RP_DASHBOARD_QTY_MEASURES = [ARRIVAL_PLAN, DEMAND_PLAN, ACT_ARRIVAL, SALES, SHPT, INVENTORY, PURCHASE, LOSS_OF_OPPORTUNITY, INV_TURNOVER, INV_TURNOVER_COGS, VS_ARRIVAL_PLAN, FILLRATE];

const digit = 5;
const RpDashboard = ({ viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const { RP } = CodeUtil.module;
  const [isLoad, setIsLoad] = useState(false);

  const [locationFilter, setLocationFilter] = useState(reduxViewState?.locationInhouse ? reduxViewState.locationInhouse : null);
  const [locationVendorFilter, setLocationVendorFilter] = useState(reduxViewState?.locationSupplier ? reduxViewState.locationSupplier : null);
  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? {});
  const [measureType, setMeasureType] = useState(reduxViewState?.measureType ?? AMT);
  const ytdHrchyName = useRef("ALL");

  const [ytdDashboardResult, setYtdDashboardResult] = useState([]);
  const [trendDataResult, setTrendDataResult] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [currency, setCurrency] = useState(null);

  const allMeasures = useMemo(() => {
    return {
      qty: RP_DASHBOARD_QTY_MEASURES,
      amt: RP_DASHBOARD_AMT_MEASURES
    };
  }, [])

  const stackBarColumDefs = useMemo(() => [
    {
      headerName: "realCategory", field: "hrchyNm", flex: 2,
      type: ["nonEditableColumn", "defaultValueFormatter"],
    },
    {
      headerName: "",
      field: "stackBarChartData",
      cellRenderer: "stackBarChartCellRenderer",
      cellRendererParams: {
        scmModule: RP,
        flipZIndex: false,
        currencyCd: currency
      },
      type: ["nonEditableColumn", "defaultValueFormatter"],
      flex: 4,
    },
  ], [RP, currency]);

  function convertNullToZero(arr) {
    return arr.map(value => value === null ? 0 : value);
  }

  function floorToFixed(value, n) {
    const multiplier = Math.pow(10, n);
    return Math.floor(value * multiplier) / multiplier;
  }

  const calculateArray = (values1, values2, operator) => {
    const operations = {
      minus: (a, b) => {
        const result = a - b;
        return isNaN(result) ? 0 : result;
      },
      plus: (a, b) => {
        const result = a + b;
        return isNaN(result) ? 0 : result;
      },
      multiply: (a, b) => {
        const result = a * b;
        return isNaN(result) ? 0 : result;
      },
      divide: (a, b) => {
        if (b === 0) return 0;
        const result = floorToFixed(a / b, 4);
        return isNaN(result) ? 0 : result;
      }
    };
    return values1.map((val1, index) => {
      const value1 = val1 ?? 0;
      const value2 = values2[index] ?? 0;
      return operations[operator](value1, value2);
    });
  };

  const getRequestParams = () => {
    const params = {};

    if (!isEmpty(locationFilter)) {
      params.locCd = locationFilter.locCd;
    }

    if (!isEmpty(locationVendorFilter)) {
      params.vendorLocCd = locationVendorFilter.locCd;
    }

    if (filterValue?.category) {
      params.itemHrchyCd = filterValue.category.hrchyCd;
    }
    params.currentDate = new ZDate();

    return params;
  }

  const addItemToTrendResult = (measure, resultArray, type, itemHrchyNm, locNm, values, customTotal = null) => {
    resultArray.push({
      measure,
      type,
      itemHrchyNm,
      locNm,
      values,
      customTotal
    });
  };


  // 필요한 계산 함수들
  const calculateValues = (res, headerDate) => {
    // AMT
    const currentDate = new ZDate();
    const currentYearMonth = currentDate.toISOString().slice(0, 7);
    const index = headerDate.indexOf(currentYearMonth);

    const adjustedBohAmtPivotData = [...res.bohAmtPivotData];
    if (index > 0) {
      adjustedBohAmtPivotData[index] = res.invAmtPivotData[index - 1] ?? adjustedBohAmtPivotData[index];
    }

    // profit : sales - cogs
    const grossProfitAmtValue = calculateArray(res.salesAmtPivotData, res.cogsPivotData, 'minus');
    // loss of opportunity : demand - sales
    const lossOfOpportunityAmtValue = calculateArray(res.demandAmtPivotData, res.salesAmtPivotData, 'minus');
    // gross margin : profit / sales
    const grossMarginAmtValue = calculateArray(grossProfitAmtValue, res.salesAmtPivotData, 'divide');
    // inventory turnover : sales / ((boh + eoh) / 2)
    const bohEohAmt = calculateArray(res.invAmtPivotData, adjustedBohAmtPivotData, 'plus').map(value => value / 2);
    const invTurnoverAmtValue = calculateArray(res.salesAmtPivotData, bohEohAmt, 'divide');
    const invTurnoverCogsValue = calculateArray(res.cogsPivotData, bohEohAmt, 'divide');
    // arrival vs plan : arrival / arrival plan
    const vsArrivalPlanAmtValue = calculateArray(res.arrivalActAmtPivotData, res.arrivalPlanAmtPivotData, 'divide');
    // crossover : inventory turnover * gross margin
    const crossoverRatioAmtValue = calculateArray(invTurnoverAmtValue, grossMarginAmtValue, 'multiply');
    // fill rate : past : actual sales, future : rp sales / demand
    const fillRateAmtValue = calculateArray(res.salesAmtPivotData, res.demandAmtPivotData, 'divide');

    // QTY
    const lossOfOpportunityQtyValue = calculateArray(res.demandQtyPivotData, res.salesQtyPivotData, 'minus');
    const bohEohQty = calculateArray(res.invQtyPivotData, res.bohQtyPivotData, 'plus').map(value => value / 2);
    const invTurnoverQtyValue = calculateArray(res.salesQtyPivotData, bohEohQty, 'divide');
    const vsArrivalPlanQtyValue = calculateArray(res.arrivalActQtyPivotData, res.arrivalPlanQtyPivotData, 'divide');
    const fillRateQtyValue = calculateArray(res.salesQtyPivotData, res.demandQtyPivotData, 'divide');

    return {
      grossProfitAmtValue,
      lossOfOpportunityAmtValue,
      grossMarginAmtValue,
      invTurnoverAmtValue,
      invTurnoverCogsValue,
      vsArrivalPlanAmtValue,
      crossoverRatioAmtValue,
      fillRateAmtValue,
      lossOfOpportunityQtyValue,
      invTurnoverQtyValue,
      vsArrivalPlanQtyValue,
      fillRateQtyValue
    };
  };

  const createStackBarChartData = (actualQty, planQty) => [
    actualQty === null ? 0 : actualQty,
    planQty === null ? 0 : planQty,
    (planQty === null || planQty === 0 || isNaN(actualQty / planQty)) ? 0 : floorToFixed((actualQty / planQty * 100), 2)
  ];

  const addItemToYtdResult = (tabName, targetArray, name, actualQty, planQty, type) => {
    targetArray.push({
      tabName,
      type,
      hrchyNm: name,
      stackBarChartData: createStackBarChartData(actualQty, planQty)
    });
  };

  const setYtdData = (ytdRes) => {
    const ytdDashboardData = {
      // key: "YTD",
      itemHrchy: { [ARRIVAL]: [], [PURCHASE]: [] },
      locationInhouse: { [ARRIVAL]: [], [PURCHASE]: [] },
      locationSupplier: { [SUPPLY]: [], [PURCHASE]: [] }
    };

    // itemHrchy 처리
    ytdRes.itemHrchy.forEach(res => {
      addItemToYtdResult(RP_ARRIVAL, ytdDashboardData.itemHrchy[ARRIVAL], res.itemHrchyName, res.ytdArrivalActQty, res.ytdArrivalPlanQty, QTY);
      addItemToYtdResult(RP_ARRIVAL, ytdDashboardData.itemHrchy[ARRIVAL], res.itemHrchyName, res.ytdArrivalActAmt, res.ytdArrivalPlanAmt, AMT);
    });

    ytdRes.purchaseBudgetItemHrchy.forEach(res => {
      addItemToYtdResult(RP_PURCHASE, ytdDashboardData.itemHrchy[PURCHASE], res.itemHrchyName, res.ytdPurchasePlanQty, res.ytdPurchaseBudgetQty, QTY);
      addItemToYtdResult(RP_PURCHASE, ytdDashboardData.itemHrchy[PURCHASE], res.itemHrchyName, res.ytdPurchasePlanAmt, res.ytdPurchaseBudgetAmt, AMT);
    });

    // loc 처리
    ytdRes.loc.forEach(res => {
      addItemToYtdResult(RP_ARRIVAL, ytdDashboardData.locationInhouse[ARRIVAL], res.locName, res.ytdArrivalActQty, res.ytdArrivalPlanQty, QTY);
      addItemToYtdResult(RP_ARRIVAL, ytdDashboardData.locationInhouse[ARRIVAL], res.locName, res.ytdArrivalActAmt, res.ytdArrivalPlanAmt, AMT);
    });

    ytdRes.purchaseBudgetLoc.forEach(res => {
      addItemToYtdResult(RP_PURCHASE, ytdDashboardData.locationInhouse[PURCHASE], res.locName, res.ytdPurchasePlanQty, res.ytdPurchaseBudgetQty, QTY);
      addItemToYtdResult(RP_PURCHASE, ytdDashboardData.locationInhouse[PURCHASE], res.locName, res.ytdPurchasePlanAmt, res.ytdPurchaseBudgetAmt, AMT);
    });

    ytdRes.locVendor.forEach(res => {
      addItemToYtdResult(RP_SUPPLY, ytdDashboardData.locationSupplier[SUPPLY], res.locName, res.ytdArrivalActQty, res.ytdArrivalPlanQty, QTY);
      addItemToYtdResult(RP_SUPPLY, ytdDashboardData.locationSupplier[SUPPLY], res.locName, res.ytdArrivalActAmt, res.ytdArrivalPlanAmt, AMT);
    })

    ytdRes.purchaseBudgetLocVendor.forEach(res => {
      addItemToYtdResult(RP_PURCHASE, ytdDashboardData.locationSupplier[PURCHASE], res.locName, res.ytdPurchasePlanQty, res.ytdPurchaseBudgetQty, QTY);
      addItemToYtdResult(RP_PURCHASE, ytdDashboardData.locationSupplier[PURCHASE], res.locName, res.ytdPurchasePlanAmt, res.ytdPurchaseBudgetAmt, AMT);
    });
    return ytdDashboardData;
  }

  const makeTrendData = (monthlyRes, headerDate) => {
    const monthlyResult = {
      key: "MonthlyByTrend",
      baseDate: headerDate,
      [ARRIVAL_PLAN]: [],
      [DEMAND_PLAN]: [],
      [ACT_ARRIVAL]: [],
      [SALES]: [],
      [SHPT]: [],
      [INVENTORY]: [],
      [PURCHASE]: [],
      [GROSS_PROFIT]: [],
      [LOSS_OF_OPPORTUNITY]: [],
      [INV_TURNOVER]: [],
      [INV_TURNOVER_COGS]: [],
      [GROSS_MARGIN]: [],
      [CROSSOVER_RATIO]: [],
      [VS_ARRIVAL_PLAN]: [],
      [FILLRATE]: [],
    };

    monthlyRes.forEach(res => {
      const {
        grossProfitAmtValue,
        lossOfOpportunityAmtValue,
        grossMarginAmtValue,
        invTurnoverAmtValue,
        invTurnoverCogsValue,
        vsArrivalPlanAmtValue,
        crossoverRatioAmtValue,
        fillRateAmtValue,
        lossOfOpportunityQtyValue,
        invTurnoverQtyValue,
        vsArrivalPlanQtyValue,
        fillRateQtyValue
      } = calculateValues(res, headerDate);

      // Arrival Plan
      addItemToTrendResult(ARRIVAL_PLAN, monthlyResult[ARRIVAL_PLAN], "qty", res.itemHrchyName, res.locName, convertNullToZero(res.arrivalPlanQtyPivotData));
      addItemToTrendResult(ARRIVAL_PLAN, monthlyResult[ARRIVAL_PLAN], "amt", res.itemHrchyName, res.locName, convertNullToZero(res.arrivalPlanAmtPivotData));

      // Arrival
      addItemToTrendResult(ACT_ARRIVAL, monthlyResult[ACT_ARRIVAL], "qty", res.itemHrchyName, res.locName, convertNullToZero(res.arrivalActQtyPivotData));
      addItemToTrendResult(ACT_ARRIVAL, monthlyResult[ACT_ARRIVAL], "amt", res.itemHrchyName, res.locName, convertNullToZero(res.arrivalActAmtPivotData));

      // Sales
      addItemToTrendResult(SALES, monthlyResult[SALES], "qty", res.itemHrchyName, res.locName, convertNullToZero(res.salesQtyPivotData));
      addItemToTrendResult(SALES, monthlyResult[SALES], "amt", res.itemHrchyName, res.locName, convertNullToZero(res.salesAmtPivotData));

      // Shipment
      addItemToTrendResult(SHPT, monthlyResult[SHPT], "qty", res.itemHrchyName, res.locName, convertNullToZero(res.shptQtyPivotData));
      addItemToTrendResult(SHPT, monthlyResult[SHPT], "amt", res.itemHrchyName, res.locName, convertNullToZero(res.shptAmtPivotData));

      // Demand Plan
      addItemToTrendResult(DEMAND_PLAN, monthlyResult[DEMAND_PLAN], "qty", res.itemHrchyName, res.locName, convertNullToZero(res.demandQtyPivotData));
      addItemToTrendResult(DEMAND_PLAN, monthlyResult[DEMAND_PLAN], "amt", res.itemHrchyName, res.locName, convertNullToZero(res.demandAmtPivotData));

      // Inventory
      addItemToTrendResult(INVENTORY, monthlyResult[INVENTORY], "qty", res.itemHrchyName, res.locName, convertNullToZero(res.invQtyPivotData));
      addItemToTrendResult(INVENTORY, monthlyResult[INVENTORY], "amt", res.itemHrchyName, res.locName, convertNullToZero(res.invAmtPivotData));

      // Purchase Budget
      addItemToTrendResult(PURCHASE, monthlyResult[PURCHASE], "qty", res.itemHrchyName, res.locName, res.purchaseActQtyPivotData);
      addItemToTrendResult(PURCHASE, monthlyResult[PURCHASE], "amt", res.itemHrchyName, res.locName, res.purchaseActAmtPivotData);

      // Calculate
      addItemToTrendResult(GROSS_PROFIT, monthlyResult[GROSS_PROFIT], "amt", res.itemHrchyName, res.locName, grossProfitAmtValue);
      addItemToTrendResult(LOSS_OF_OPPORTUNITY, monthlyResult[LOSS_OF_OPPORTUNITY], "amt", res.itemHrchyName, res.locName, lossOfOpportunityAmtValue);

      // inventory turnover total: sales total / inventory average
      const salesQtyTotal = sum(monthlyResult[SALES]?.filter(result => result.type === "qty")?.map(result => sum(result.values))) || 0;
      const salesAmtTotal = sum(monthlyResult[SALES]?.filter(result => result.type === "amt")?.map(result => sum(result.values))) || 0;
      const salesCogsTotal = sum(convertNullToZero(res.cogsPivotData)) || 0;
      const invQtyList = monthlyResult[INVENTORY]?.filter(result => result.type === "qty") || [];
      const invAmtList = monthlyResult[INVENTORY]?.filter(result => result.type === "amt") || [];

      const invAverageQty = invQtyList?.length > 0 ? mean(invQtyList?.[0]?.values) : 0;
      const invAverageAmt = invAmtList?.length > 0 ? mean(invAmtList?.[0]?.values) : 0;

      let invTurnoverQtyTotal = salesQtyTotal / invAverageQty;
      let invTurnoverAmtTotal = salesAmtTotal / invAverageAmt;
      let invTurnoverCogsTotal = salesCogsTotal / invAverageAmt;

      if (isNumber(invTurnoverQtyTotal)) {
        invTurnoverQtyTotal = invTurnoverQtyTotal.toFixed(digit);
      }

      if (isNumber(invTurnoverAmtTotal)) {
        invTurnoverAmtTotal = invTurnoverAmtTotal.toFixed(digit);
      }

      if (isNumber(invTurnoverCogsTotal)) {
        invTurnoverCogsTotal = invTurnoverCogsTotal.toFixed(digit);
      }

      addItemToTrendResult(INV_TURNOVER, monthlyResult[INV_TURNOVER], "qty", res.itemHrchyName, res.locName, invTurnoverQtyValue, invTurnoverQtyTotal);
      addItemToTrendResult(INV_TURNOVER, monthlyResult[INV_TURNOVER], "amt", res.itemHrchyName, res.locName, invTurnoverAmtValue, invTurnoverAmtTotal);
      addItemToTrendResult(INV_TURNOVER_COGS, monthlyResult[INV_TURNOVER_COGS], "amt",  res.itemHrchyName, res.locName, invTurnoverCogsValue, invTurnoverCogsTotal);

      const profitAmtTotal = sum(monthlyResult[GROSS_PROFIT]?.filter(result => result.type === "amt")?.map(result => sum(result.values))) || 0;
      const grossMarginAmtTotal = divide(profitAmtTotal, salesAmtTotal);
      addItemToTrendResult(GROSS_MARGIN, monthlyResult[GROSS_MARGIN], "amt", res.itemHrchyName, res.locName, grossMarginAmtValue, grossMarginAmtTotal);

      const crossoverRatioAmtTotal = invTurnoverAmtTotal * grossMarginAmtTotal;
      addItemToTrendResult(CROSSOVER_RATIO, monthlyResult[CROSSOVER_RATIO], "amt", res.itemHrchyName, res.locName, crossoverRatioAmtValue, crossoverRatioAmtTotal);

      const totalAmtShipment = sum(monthlyResult[SHPT]?.filter(result => result.type === "amt")?.map(result => sum(result.values))) || 0;
      const totalAmtDemand = sum(monthlyResult[DEMAND_PLAN]?.filter(result => result.type === "amt")?.map(result => sum(result.values))) || 0;
      const totalQtyShipment = sum(monthlyResult[SHPT]?.filter(result => result.type === "qty")?.map(result => sum(result.values))) || 0;
      const totalQtyDemand = sum(monthlyResult[DEMAND_PLAN]?.filter(result => result.type === "qty")?.map(result => sum(result.values))) || 0;
      // Total Shipments / Total Demand Plan
      const fillRateAmtTotal = divide(totalAmtShipment, totalAmtDemand);
      const fillRateQtyTotal = divide(totalQtyShipment, totalQtyDemand);
      addItemToTrendResult(FILLRATE, monthlyResult[FILLRATE], "amt", res.itemHrchyName, res.locName, fillRateAmtValue, fillRateAmtTotal);
      addItemToTrendResult(FILLRATE, monthlyResult[FILLRATE], "qty", res.itemHrchyName, res.locName, fillRateQtyValue, fillRateQtyTotal);

      addItemToTrendResult(LOSS_OF_OPPORTUNITY, monthlyResult[LOSS_OF_OPPORTUNITY], "qty", res.itemHrchyName, res.locName, lossOfOpportunityQtyValue);

      // Total Actual Receipts/Inflows / Total Planned Receipts/Inflows
      const totalPlanVsArrAmtPer = divide(sum(res.arrivalActAmtPivotData), sum(res.arrivalPlanAmtPivotData));
      addItemToTrendResult(VS_ARRIVAL_PLAN, monthlyResult[VS_ARRIVAL_PLAN], "amt", res.itemHrchyName, res.locName, vsArrivalPlanAmtValue, totalPlanVsArrAmtPer);
      const totalPlanVsArrQtyPer = divide(sum(res.arrivalActQtyPivotData), sum(res.arrivalPlanQtyPivotData));
      addItemToTrendResult(VS_ARRIVAL_PLAN, monthlyResult[VS_ARRIVAL_PLAN], "qty", res.itemHrchyName, res.locName, vsArrivalPlanQtyValue, totalPlanVsArrQtyPer);
    });

    return monthlyResult;
  }

  const calculateTotalWithRatio = (data) => {
    const initialTotals = { qty: [0, 0, 0], amt: [0, 0, 0] };

    const totals = data.reduce((acc, item) => {
      const key = item.type;
      if (key === 'qty' || key === 'amt') {
        acc[key][0] = isNaN(item.stackBarChartData[0]) ? 0 : numberUtil.decimalAdd(acc[key][0], item.stackBarChartData[0], 5);
        acc[key][1] = isNaN(item.stackBarChartData[1]) ? 0 : numberUtil.decimalAdd(acc[key][1], item.stackBarChartData[1], 5);
      }
      return acc;
    }, initialTotals);

    totals.qty[2] = totals.qty[1] !== 0 ? floorToFixed((totals.qty[0] / totals.qty[1]) * 100, 2) : 0;
    totals.amt[2] = totals.amt[1] !== 0 ? floorToFixed((totals.amt[0] / totals.amt[1]) * 100, 2) : 0;

    return totals;
  };

  const addAllCategoryWithRatio = (data, category) => {
    const { qty: totalQty, amt: totalAmt } = calculateTotalWithRatio(data);
    return [
      ...data,
      { tabName: data[0]?.tabName ?? "", type: 'qty', hrchyNm: category === 'itemHrchy' ? ytdHrchyName.current : 'ALL', stackBarChartData: totalQty },
      { tabName: data[0]?.tabName ?? "", type: 'amt', hrchyNm: category === 'itemHrchy' ? ytdHrchyName.current : 'ALL', stackBarChartData: totalAmt },
    ];
  };

  const sortByData = (a, b) => {
    // HrchyNm이 "ALL"인 경우 항상 먼저 오도록 설정
    if (a.hrchyNm === "ALL" || a.hrchyNm === ytdHrchyName.current) return -1;
    if (b.hrchyNm === "ALL" || a.hrchyNm === ytdHrchyName.current) return 1;

    // Plan 기준 정렬
    const valueComparison = b.stackBarChartData[1] - a.stackBarChartData[1]

    // 값이 같을 경우 Actual 기준 정렬
    if (valueComparison === 0) {
      return b.stackBarChartData[0] - a.stackBarChartData[0]; // a.hrchyNm.localeCompare(b.hrchyNm);
    }

    return valueComparison;
  };

  const processCategory = (categoryData, category) => {
    if (!isEmpty(categoryData) && categoryData.length > 2) {
      const dataWithAll = addAllCategoryWithRatio(categoryData, category);
      return dataWithAll.sort(sortByData);
    }
    return categoryData;
  };


  const addALlAndsortByYtd = (ytdResult) => {
    const categories = ['itemHrchy', 'locationInhouse', 'locationSupplier'];
    const types = [
      { category: 'itemHrchy', types: [ARRIVAL, PURCHASE] },
      { category: 'locationInhouse', types: [ARRIVAL, PURCHASE] },
      { category: 'locationSupplier', types: [SUPPLY, PURCHASE] },
    ];

    const sortedData = {};

    categories.forEach((category) => {
      sortedData[category] = {};
      types
        .find((type) => type.category === category)
        .types.forEach((type) => {
          sortedData[category][type] = processCategory(ytdResult[category][type], category);
        });
    });

    return sortedData;
  }

  const setTrendGridData = (monthlyByTrend) => {
    const dataList = [];
    const header = monthlyByTrend?.baseDate;
    const measureNms = difference(keys(monthlyByTrend), ["key", "baseDate"]);
    for (const measure of measureNms) {
      const measureData = monthlyByTrend[measure];
      if (size(measureData) > 0) {
        for (const rowData of measureData) {
          const rData = {};
          rData.measure = measure;
          rData.location = rowData.locNm;
          rData.itemHrchy = rowData.itemHrchyNm;
          rData.customTotal = rowData.customTotal;

          for (let idx = 0; idx < header.length; idx++) {
            rData[header[idx]] = rowData.values?.[idx];
          }
          rData.type = rowData.type;
          dataList.push(rData);
        }
      } else {
        for (const valType of [QTY, AMT]) {
          const rData = {};
          rData.measure = measure;
          rData.location = "";
          rData.itemHrchy = "";

          for (let idx = 0; idx < header.length; idx++) {
            rData[header[idx]] = 0;
          }
          rData.type = valType;
          dataList.push(rData);
        }
      }
    }
    const gridData = groupBy(dataList, "type");
    gridData.baseDate = header;
    return gridData;
  }

  const setTrendChartData = (monthlyByTrend) => {
    const baseDate = monthlyByTrend.baseDate;
    // 각 measure에 따라 데이터를 변환.
    const transformChartData = (type) => {
      const data = {};
      const chartMeasures = allMeasures[type];
      chartMeasures.forEach((measure) => {
        const measureData = monthlyByTrend[measure].find((item) => item.type === type);
        if (measureData) {
          if (measure.toUpperCase()?.includes("PER")) {
            data[measure] = measureData.values;
          } else {
            data[measure] = measureData.values;
          }

        }
      });
      return baseDate.map((date, index) => ({
        yyyymmdd: `${date}`,
        type,
        ...chartMeasures.reduce((acc, measure) => {
          acc[measure] = data[measure]?.[index];
          return acc;
        }, {}),
      }));
    };

    const chartData = {
      qty: transformChartData(QTY),
      amt: transformChartData(AMT),
    }

    return chartData;
  }

  const setTrendDashboardResult = (monthlyResponse) => {
    const headerDate = monthlyResponse.localDateHeader.map(header => dateUtils.formatDate(header, "yyyy-MM"));
    const trendResponse = monthlyResponse.data;
    const monthlyResult = makeTrendData(trendResponse, headerDate);
    setTrendDataResult(setTrendGridData(monthlyResult));
    setChartData(setTrendChartData(monthlyResult));
  }

  const setYtdMaxDataExcludeAll = (ytdDataEntryMap) => {
    for (const entryYtdKey in ytdDataEntryMap) {
      if (Object.prototype.hasOwnProperty.call(ytdDataEntryMap, entryYtdKey)) {
        const ytdDataMap = ytdDataEntryMap[entryYtdKey];

        for (const ytd in ytdDataMap) {
          if (Object.prototype.hasOwnProperty.call(ytdDataMap, ytd)) {
            const ytdByTypeGroupObj = groupBy(ytdDataMap[ytd], "type");
            if (!isEmpty(ytdByTypeGroupObj)) {
              /* QTY */
              const qtyYtdDataArray = ytdByTypeGroupObj[QTY];
              const qtyMaxValueNotAll = Math.max(...qtyYtdDataArray.filter(data => data.hrchyNm !== "ALL" && data.hrchyNm !== ytdHrchyName.current).map(data => data?.stackBarChartData[1]));
              qtyYtdDataArray.forEach(data => {
                if (data.hrchyNm !== "ALL" || data.hrchyNm !== ytdHrchyName.current) {
                  data.domainMaxValue = qtyMaxValueNotAll;
                }
              });

              /* AMT */
              const amtYtdDataArray = ytdByTypeGroupObj[AMT];
              const amtMaxValueNotAll = Math.max(...amtYtdDataArray.filter(data => data.hrchyNm !== "ALL" && data.hrchyNm !== ytdHrchyName.current).map(data => data?.stackBarChartData[1]));
              amtYtdDataArray.forEach(data => {
                if (data.hrchyNm !== "ALL" && data.hrchyNm !== ytdHrchyName.current) {
                  data.domainMaxValue = amtMaxValueNotAll;
                }
              });
            }
          }
        }
      }
    }
  }

  const retrieveRpDashboard = () => {
    const params = getRequestParams();

    dashboardService.getRpModule(params)
      .then(response => {
        const { pivotResult, ytdResult, ytdItemhrchyNm, currency } = response.data;
        setCurrency(currency)
        ytdHrchyName.current = ytdItemhrchyNm;

        const ytdDashboardData = setYtdData(ytdResult);
        const sortedYtdDashboardData = addALlAndsortByYtd(ytdDashboardData);
        setYtdMaxDataExcludeAll(sortedYtdDashboardData);
        setYtdDashboardResult(sortedYtdDashboardData);
        setTrendDashboardResult(pivotResult);

      })
      .catch(e => {
        console.log(e);
      })
  }

  useEffect(() => {
    setIsLoad(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isLoad) {
      retrieveRpDashboard();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue?.category, locationFilter, locationVendorFilter, isLoad]);

  useEffect(() => {
    const reduxState = reduxUtil.createObjectState(viewName, { measureType });
    reduxDispatch(reduxUtil.updateViewState(reduxState));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measureType]);

  const period = useMemo(() => baseDatesToPeriod(trendDataResult?.baseDate), [trendDataResult]);

  return (
    <>
      <FilterContainer>
        <ItemHrchyTreeFilter viewName={viewName} enableSelectParent onChangeFilter={setFilterValue} />
        <LocationFilter viewName={viewName} locationValue={locationFilter} onChangeFilter={setLocationFilter} inhouseFilters={[true]} title={"locationInhouse"} />
        <LocationFilter viewName={viewName} locationValue={locationVendorFilter} onChangeFilter={setLocationVendorFilter} inhouseFilters={[false]} title={"locationSupplier"} />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <TypeToggleButton toggleType="measure" initialValue={measureType} toggleValue={setMeasureType} styles={{ mb: 1 }} />
          <DashboardPeriodCaption period={period} />
        </Box>
      </FilterContainer>      <DashboardComponent
        viewName={viewName}
        title={title}
        moduleCd={RP}
        stackBarData={ytdDashboardResult}
        stackBarColumDefs={stackBarColumDefs}
        allMeasures={allMeasures}
        chartData={chartData}
        gridData={trendDataResult}
        measureType={measureType}
        currencyCd={currency}
      />
    </>
  );
};
export default withTranslation()(RpDashboard);

